import { css, Interpolation } from 'styled-components';
import { RuleSet, Styles } from 'styled-components/dist/types';

// Define breakpoint names for better type safety
export type BreakpointKey =
  | 'mobile'
  | 'tablet'
  | 'md'
  | 'l'
  | 'xl'
  | 'xxl'
  | 'wide'
  | 'ultra';

// Define the media function type
export type MediaFunction = <P extends object = object>(
  first: TemplateStringsArray | Styles<P>,
  ...interpolations: Array<Interpolation<P>>
) => RuleSet<P>;

// Define a type for the media query collections
export type MediaQueryHelpers = Record<BreakpointKey, MediaFunction>;

/**
 * Creates a media query with the specified condition and breakpoint
 * @param condition The media query condition (max-width, min-width, etc)
 * @param breakpoint The breakpoint name from the theme
 */
const createMediaQuery = (
  condition: string,
  breakpoint: BreakpointKey,
): MediaFunction => {
  return <P extends object = object>(
    first: TemplateStringsArray | Styles<P>,
    ...interpolations: Array<Interpolation<P>>
  ): RuleSet<P> => {
    if (Array.isArray(first)) {
      // When used as a tagged template literal - this preserves Prettier formatting
      return css<P>`
        @media (${condition}: ${({ theme }) =>
            theme.breakpoints[breakpoint]}px) {
          ${css<P>(first, ...interpolations)}
        }
      `;
    }

    // When used as a function
    return css`
      @media (${condition}: ${({ theme }) => theme.breakpoints[breakpoint]}px) {
        ${css(first as any)}
      }
    `;
  };
};

/**
 * Media queries for max-width (styles apply from this breakpoint and below)
 */
export const media: MediaQueryHelpers = {
  mobile: createMediaQuery('max-width', 'mobile'),
  tablet: createMediaQuery('max-width', 'tablet'),
  md: createMediaQuery('max-width', 'md'),
  l: createMediaQuery('max-width', 'l'),
  xl: createMediaQuery('max-width', 'xl'),
  xxl: createMediaQuery('max-width', 'xxl'),
  wide: createMediaQuery('max-width', 'wide'),
  ultra: createMediaQuery('max-width', 'ultra'),
};

/**
 * Media queries for min-width (styles apply from this breakpoint and above)
 */
export const mediaMin: MediaQueryHelpers = {
  mobile: createMediaQuery('min-width', 'mobile'),
  tablet: createMediaQuery('min-width', 'tablet'),
  md: createMediaQuery('min-width', 'md'),
  l: createMediaQuery('min-width', 'l'),
  xl: createMediaQuery('min-width', 'xl'),
  xxl: createMediaQuery('min-width', 'xxl'),
  wide: createMediaQuery('min-width', 'wide'),
  ultra: createMediaQuery('min-width', 'ultra'),
};

/**
 * Media query helpers with more descriptive names
 */
export const mediaQuery = {
  /**
   * Styles apply on screens smaller than the breakpoint
   */
  lessThan: media,
  /**
   * Styles apply on screens larger than the breakpoint
   */
  greaterThan: mediaMin,
};
